/**
 * First-touch attribution and the backoffice click-stream (docs/admin/SPEC.md §3.2, §5.1).
 *
 * What is sent: page views (route templates, ids replaced by :id) and named actions — only explicit track() calls
 * or clicks on elements with data-track="name". Never form field values, key presses, template contents or
 * recipient addresses.
 *
 * Delivery: a queue flushed every 10 s or at 20 events; when the tab is hidden or closed, a keepalive fetch.
 * Not navigator.sendBeacon: it cannot set the X-MailOcean-CSRF header, and CSRF is not weakened for telemetry.
 */

const FIRST_TOUCH_KEY = 'mo_first_touch';
const MAX = 200;
const FLUSH_MS = 10_000;
const FLUSH_AT = 20;
const BATCH_MAX = 50;
const NAME_RE = /^[a-z0-9_.:/-]+$/;

export interface FirstTouch {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer?: string;
  landing_path?: string;
  first_seen_at?: string;
}

const UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
const clip = (v: string | null | undefined) => (v ? v.trim().slice(0, MAX) : undefined);

/** On the first visit before sign-in: where the visitor came from. Kept forever in this browser (first touch). */
export function captureFirstTouch(): void {
  try {
    if (localStorage.getItem(FIRST_TOUCH_KEY)) return;
    const params = new URLSearchParams(location.search);
    const touch: FirstTouch = { landing_path: clip(location.pathname) ?? '/', first_seen_at: new Date().toISOString() };
    for (const k of UTM) {
      const v = clip(params.get(k));
      if (v) touch[k] = v;
    }
    // Only an external referrer, and only origin + path: query strings may carry tokens.
    if (document.referrer) {
      const ref = new URL(document.referrer);
      if (ref.origin !== location.origin && /^https?:$/.test(ref.protocol)) touch.referrer = clip(`${ref.origin}${ref.pathname}`);
    }
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touch));
  } catch {
    // Private mode or blocked storage: no attribution, nothing breaks.
  }
}

/** Attribution for the sign-up request (only well-formed values; the server schema is strict). */
export function readFirstTouch(): FirstTouch | undefined {
  try {
    const raw = localStorage.getItem(FIRST_TOUCH_KEY);
    if (!raw) return undefined;
    const v = JSON.parse(raw) as Record<string, unknown>;
    const out: FirstTouch = {};
    for (const k of [...UTM, 'referrer', 'landing_path', 'first_seen_at'] as const) {
      const s = v[k];
      if (typeof s === 'string' && s.trim()) out[k] = s.trim().slice(0, MAX);
    }
    if (out.referrer && !/^https?:\/\/[^\s?#]+$/.test(out.referrer)) delete out.referrer;
    if (out.landing_path && !/^\/[^\s?#]*$/.test(out.landing_path)) delete out.landing_path;
    if (out.first_seen_at && Number.isNaN(Date.parse(out.first_seen_at))) delete out.first_seen_at;
    return Object.keys(out).length ? out : undefined;
  } catch {
    return undefined;
  }
}

/** '/emails/msg_4f2a…' → '/emails/:id'; addresses and numbers too. Never leaks ids or emails into the stream. */
export function normalizePath(path: string): string {
  const out = path
    .split('/')
    .map((seg) => {
      if (!seg) return seg;
      let s = seg;
      try {
        s = decodeURIComponent(seg);
      } catch {
        // keep as is
      }
      if (/^[a-z]{3}_[A-Za-z0-9]+$/.test(s) || /@/.test(s) || /^\d+$/.test(s) || s.length > 40) return ':id';
      return s.toLowerCase().replace(/[^a-z0-9_.:-]/g, '-');
    })
    .join('/');
  return (out || '/').slice(0, 120);
}

type Props = Record<string, string | number | boolean>;
interface QueuedEvent {
  kind: 'page_view' | 'action';
  name: string;
  at: string;
  props: Props;
}

let queue: QueuedEvent[] = [];
let timer: number | undefined;
let started = false;
let lastPage: string | null = null;

function cleanProps(props: Props = {}): Props {
  const out: Props = {};
  for (const [k, v] of Object.entries(props).slice(0, 10)) {
    if (typeof v === 'string') out[k] = v.slice(0, MAX);
    else if (typeof v === 'number' && Number.isFinite(v)) out[k] = v;
    else if (typeof v === 'boolean') out[k] = v;
  }
  return out;
}

function push(e: QueuedEvent): void {
  if (!started || !NAME_RE.test(e.name) || e.name.length > 120) return;
  queue.push(e);
  if (queue.length > 500) queue = queue.slice(-500);
  if (queue.length >= FLUSH_AT) flush();
}

/** A named action: track('domain.verify_click', { domain_id }). */
export function track(name: string, props?: Props): void {
  push({ kind: 'action', name, at: new Date().toISOString(), props: cleanProps(props) });
}

export function trackPage(pathname: string): void {
  const name = normalizePath(pathname);
  const from = lastPage;
  lastPage = name;
  push({ kind: 'page_view', name, at: new Date().toISOString(), props: from ? { from } : {} });
}

/** Sends what is queued; keepalive lets the request finish while the tab is closing. Telemetry never throws. */
export function flush(keepalive = false): void {
  while (queue.length) {
    const events = queue.splice(0, BATCH_MAX);
    try {
      void fetch('/dashboard/v1/activity', {
        method: 'POST',
        credentials: 'same-origin',
        keepalive,
        headers: { 'Content-Type': 'application/json', 'X-MailOcean-CSRF': '1' },
        body: JSON.stringify({ events }),
      }).catch(() => undefined);
    } catch {
      // ignore
    }
  }
}

function onClick(ev: MouseEvent): void {
  const el = (ev.target as Element | null)?.closest?.('[data-track]');
  const name = el?.getAttribute('data-track');
  if (!name) return;
  const id = el?.getAttribute('data-track-id');
  track(name, id ? { id } : undefined);
}

function onHide(): void {
  if (document.visibilityState === 'hidden') flush(true);
}

const onPageHide = () => flush(true);

/** Started by the signed-in layout; stopped on sign-out so nothing is queued for a session that ended. */
export function startActivity(): void {
  if (started) return;
  started = true;
  timer = window.setInterval(() => flush(), FLUSH_MS);
  document.addEventListener('click', onClick, true);
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', onPageHide);
}

export function stopActivity(): void {
  if (!started) return;
  flush(true);
  started = false;
  lastPage = null;
  if (timer) window.clearInterval(timer);
  document.removeEventListener('click', onClick, true);
  document.removeEventListener('visibilitychange', onHide);
  window.removeEventListener('pagehide', onPageHide);
}
